import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { ToastContainer, toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import colors from "assets/theme/base/colors";
import "react-toastify/dist/ReactToastify.css";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import "../campus/Campus.css";
import { hasAdmissionPermission } from "hooks/permissions";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const { info } = colors;

const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const MeritListTemplates = () => {
  // State variables
  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    is_form_no_show: false,
    is_student_name_show: false,
    is_father_name_show: false,
    is_DOB_show: false,
    is_age_show: false,
    matric_marks_show: false,
    matric_score_show: false,
    inter_marks_show: false,
    inter_score_show: false,
    bsc_marks_show: false,
    bsc_score_show: false,
    bs_marks_show: false,
    bs_scores_show: false,
    ma_marks_show: false,
    mphil_marks_show: false,
    description: "",
    hafiz_show: false,
    merit_show: false,
    remarks_show: false,
    last_date_show: false,
    is_approval_needed: false,
    hafiz_marks: "",
    hafiz_apply_on: "matric"
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [formErrors, setFormErrors] = useState({ name: "", hafiz_marks: "" });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // ReactQuill configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent', 'link', 'image'
  ];

  // Get all templates
  const getTemplates = async () => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(`${BASE_URL}/application/get_merit_list_templates`, requestOptions);
      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch templates");
    }
  };

  // Add template
  const addTemplate = async () => {
    try {
      const response = await fetch(`${BASE_URL}/application/create_merit_list_template`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to add template");
      }

      const newTemplate = await response.json();
      setTemplates([...templates, newTemplate]);
      getTemplates();
      toast.success("Template added successfully");
      resetForm();
    } catch (error) {
      toast.error("Failed to add template");
    } finally {
      setShowAddModal(false);
    }
  };

  // Delete template
  const deleteTemplate = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/application/delete_merit_list_template/${currentTemplateId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete template");
      }

      setTemplates(templates.filter((template) => template.id !== currentTemplateId));
      toast.success("Template deleted successfully");
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    } finally {
      setShowDeleteModal(false);
    }
  };

  // Update template
  const updateTemplate = async () => {
    const data = { id: currentTemplateId, ...formData };
    try {
      const response = await fetch(`${BASE_URL}/application/update_merit_list_template`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.message || "Failed to update template");
      }

      toast.success("Template updated successfully");
    } catch (error) {
      console.error("Error updating template:", error);
      toast.error("Failed to update template");
    } finally {
      getTemplates();
      setShowUpdateModal(false);
      resetForm();
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      is_form_no_show: false,
      is_student_name_show: false,
      is_father_name_show: false,
      is_DOB_show: false,
      is_age_show: false,
      matric_marks_show: false,
      matric_score_show: false,
      inter_marks_show: false,
      inter_score_show: false,
      bsc_marks_show: false,
      bsc_score_show: false,
      bs_marks_show: false,
      bs_scores_show: false,
      ma_marks_show: false,
      ma_score_show: false,
      mphil_marks_show: false,
      mphil_score_show: false,
      test_marks_show: false,
      test_score_show: false,
      description: "",
      hafiz_show: false,
      merit_show: false,
      remarks_show: false,
      last_date_show: false,
      is_approval_needed: false,
      hafiz_marks: "",
      hafiz_apply_on: "matric"
    });
    setFormErrors({ name: "", hafiz_marks: "" });
  };

  // Search functionality
  const handleSearchKeywordChange = (e) => {
    const searchTerm = e.target.value.trim().toLowerCase();
    setSearchKeyword(searchTerm);

    if (searchTerm === "") {
      getTemplates();
    } else {
      const filteredTemplates = templates.filter((template) => {
        return template.name.toLowerCase().includes(searchTerm);
      });
      setTemplates(filteredTemplates);
    }
  };

  // Validation
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = "* Name is Required";
    }
    if (formData.hafiz_marks && isNaN(formData.hafiz_marks)) {
      errors.hafiz_marks = "* Hafiz marks must be a number";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Modal handlers
  const handleAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleUpdateModal = (id) => {
    setCurrentTemplateId(id);
    const template = templates.find((t) => t.id === id);
    if (template) {
      setFormData({
        name: template.name || "",
        is_form_no_show: template.is_form_no_show || false,
        is_student_name_show: template.is_student_name_show || false,
        is_father_name_show: template.is_father_name_show || false,
        is_DOB_show: template.is_DOB_show || false,
        is_age_show: template.is_age_show || false,
        matric_marks_show: template.matric_marks_show || false,
        matric_score_show: template.matric_score_show || false,
        inter_marks_show: template.inter_marks_show || false,
        inter_score_show: template.inter_score_show || false,
        bsc_marks_show: template.bsc_marks_show || false,
        bsc_score_show: template.bsc_score_show || false,
        bs_marks_show: template.bs_marks_show || false,
        bs_scores_show: template.bs_scores_show || false,
        ma_marks_show: template.ma_marks_show || false,
        ma_score_show: template.ma_score_show || false,
        mphil_marks_show: template.mphil_marks_show || false,
        mphil_score_show: template.mphil_score_show || false,
        test_marks_show: template.test_marks_show || false,
        test_score_show: template.test_score_show || false,
        description: template.description || "",
        hafiz_show: template.hafiz_show || false,
        merit_show: template.merit_show || false,
        remarks_show: template.remarks_show || false,
        last_date_show: template.last_date_show || false,
        is_approval_needed: template.is_approval_needed || false,
        hafiz_marks: template.hafiz_marks || "",
        hafiz_apply_on: template.hafiz_apply_on || "matric"
      });
    }
    setShowUpdateModal(true);
  };

  const handleDeleteModal = (id) => {
    setCurrentTemplateId(id);
    setShowDeleteModal(true);
  };

  const handleViewModal = (id) => {
    const template = templates.find((t) => t.id === id);
    setSelectedTemplate(template);
    setShowViewModal(true);
  };

  const handleOkAdd = () => {
    if (validateForm()) {
      addTemplate();
    }
  };

  const handleOkUpdate = () => {
    if (validateForm()) {
      updateTemplate();
    }
  };

  const handleCancelModal = () => {
    setShowAddModal(false);
    setShowUpdateModal(false);
    resetForm();
  };

  // Initial render
  useEffect(() => {
    getTemplates();
  }, [isUpdating]);

  const columns = [
    { Header: "Sr.No", accessor: "serial", align: "center" },
    { Header: "Template Name", accessor: "template_name", align: "center" },
    { Header: "Approval Needed", accessor: "approval_needed", align: "center" },
    { Header: "Actions", accessor: "actions", align: "center" },
  ];

  const rows = templates.map((template, index) => ({
    serial: index + 1,
    template_name: template.name,
    approval_needed: template.is_approval_needed ? "Yes" : "No",
    actions: (
      <>
        {hasAdmissionPermission("MeritListTemplate", "UPDATE") && (
          <IconButton onClick={() => handleUpdateModal(template.id)}>
            <EditIcon className="icon-button-edit" />
          </IconButton>
        )}
        {hasAdmissionPermission("MeritListTemplate", "READ") && (
          <IconButton onClick={() => handleViewModal(template.id)}>
            <VisibilityOutlinedIcon className="icon-button-view" />
          </IconButton>
        )}
        {hasAdmissionPermission("MeritListTemplate", "DELETE") && (
          <IconButton onClick={() => handleDeleteModal(template.id)}>
            <DeleteIcon className="icon-button-delete" />
          </IconButton>
        )}
      </>
    ),
  }));

  // Checkbox field configurations
  const checkboxFields = [
    { key: 'is_form_no_show', label: 'Form No Show' },
    { key: 'is_student_name_show', label: 'Student Name Show' },
    { key: 'is_father_name_show', label: 'Father Name Show' },
    { key: 'is_DOB_show', label: 'DOB Show' },
    { key: 'is_age_show', label: 'Age Show' },
    { key: 'matric_marks_show', label: 'Matric Marks Show' },
    { key: 'matric_score_show', label: 'Matric Score Show' },
    { key: 'inter_marks_show', label: 'Inter Marks Show' },
    { key: 'inter_score_show', label: 'Inter Score Show' },
    { key: 'bsc_marks_show', label: 'BSC Marks Show' },
    { key: 'bsc_score_show', label: 'BSC Score Show' },
    { key: 'bs_marks_show', label: 'BS Marks Show' },
    { key: 'bs_scores_show', label: 'BS Scores Show' },
    { key: 'ma_marks_show', label: 'MA Marks Show' },
    { key: 'ma_score_show', label: 'MA Score Show' },
    { key: 'mphil_marks_show', label: 'MPhil Marks Show' },
    { key: 'mphil_score_show', label: 'MPhil Score Show' },
    { key: 'test_marks_show', label: 'Test Marks Show' },
    { key: 'test_score_show', label: 'Test Score Show' },
    { key: 'hafiz_show', label: 'Hafiz Show' },
    { key: 'merit_show', label: 'Merit Show' },
    { key: 'remarks_show', label: 'Remarks Show' },
    { key: 'last_date_show', label: 'Last Date Show' },
    { key: 'is_approval_needed', label: 'Approval Needed' },
  ];

  const renderFormFields = () => (
    <div className="row">
      <div className="col-md-6 left-fields">
        <div className="form-outline mt-3">
          <label>Template Name</label>
          <input
            name="name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="form-control form-control-lg"
            placeholder="Enter Template Name"
          />
          <div className="error-message1">{formErrors.name}</div>
        </div>

        <div className="form-outline mt-3">
          <label>Hafiz Marks</label>
          <input
            name="hafiz_marks"
            type="number"
            value={formData.hafiz_marks}
            onChange={(e) => handleInputChange('hafiz_marks', e.target.value)}
            className="form-control form-control-lg"
            placeholder="Enter Hafiz Marks"
          />
          <div className="error-message1">{formErrors.hafiz_marks}</div>
        </div>

        <div className="form-outline mt-3">
          <FormControl fullWidth>
            <InputLabel>Hafiz Apply On</InputLabel>
            <Select
              value={formData.hafiz_apply_on}
              onChange={(e) => handleInputChange('hafiz_apply_on', e.target.value)}
              label="Hafiz Apply On"
              style={{height: '45px'}}
            >
              <MenuItem value="matric">Matric</MenuItem>
              <MenuItem value="inter">Inter</MenuItem>
              <MenuItem value="bs">BS</MenuItem>
              <MenuItem value="mphil">MPhil</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className="form-outline mt-3">
          <label>Description</label>
          <ReactQuill
            theme="snow"
            value={formData.description}
            onChange={(value) => handleInputChange('description', value)}
            modules={quillModules}
            formats={quillFormats}
            style={{ height: '200px', marginBottom: '50px' }}
          />
        </div>
      </div>

      <div className="col-md-6 mt-3">
        <label style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>
          Display Options
        </label>
        <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '10px', border: '1px solid #ddd', borderRadius: '5px' }}>
          {checkboxFields.map((field) => (
            <FormControlLabel
              key={field.key}
              control={
                <Checkbox
                  checked={formData[field.key]}
                  onChange={(e) => handleInputChange(field.key, e.target.checked)}
                  color="primary"
                />
              }
              label={field.label}
              style={{ display: 'block', marginBottom: '8px' }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card className="card1">
              <MDBox
                mx={0}
                mt={0}
                py={2}
                px={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Paper className="search-paper" component="form">
                  <InputBase
                    sx={{ ml: 1, flex: 1, fontSize: "16px" }}
                    placeholder="Search Templates"
                    inputProps={{ "aria-label": "search templates" }}
                    value={searchKeyword}
                    onChange={handleSearchKeywordChange}
                  />
                  <SearchIcon className="search-icon" />
                </Paper>
                {hasAdmissionPermission("MeritListTemplate", "CREATE") && (
                  <Button
                    className="add-button"
                    variant="filled"
                    endIcon={<AddIcon className="add-icon" />}
                    onClick={handleAddModal}
                    color="info.main"
                  >
                    Add 
                  </Button>
                )}
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Add Template Dialog */}
      <Dialog
        open={showAddModal}
        onClose={handleCancelModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle className="dialog-title">
          Add Merit List Template
        </DialogTitle>
        <DialogContent className="dialog-content">
          {renderFormFields()}
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={handleCancelModal}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={handleOkAdd}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Template Dialog */}
      <Dialog
        open={showUpdateModal}
        onClose={handleCancelModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle className="dialog-title">
          Update Merit List Template
        </DialogTitle>
        <DialogContent className="dialog-content">
          {renderFormFields()}
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={handleCancelModal}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={handleOkUpdate}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Template Dialog */}
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        PaperProps={{
          sx: {
            width: "450px",
            borderRadius: "20px",
            opacity: 1,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle className="dialog-delete">Delete Template</DialogTitle>
        <DialogContent className="content-delete">
          Are you sure you want to delete this merit list template?
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={deleteTemplate}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Template Dialog */}
      <Dialog
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle className="dialog-delete">
          View Merit List Template
        </DialogTitle>
        <DialogContent className="dialog-content">
          {selectedTemplate && (
            <div className="row">
              <div className="col-md-6">
                <div className="flex-container">
                  <strong className="label1">Template Name:</strong>
                  <span className="value1">{selectedTemplate.name}</span>
                </div>
                <div className="flex-container">
                  <strong className="label1">Hafiz Marks:</strong>
                  <span className="value1">{selectedTemplate.hafiz_marks || 'Not Set'}</span>
                </div>
                <div className="flex-container">
                  <strong className="label1">Hafiz Apply On:</strong>
                  <span className="value1">{selectedTemplate.hafiz_apply_on || 'matric'}</span>
                </div>
                <div className="flex-container">
                  <strong className="label1">Description:</strong>
                  <div className="value1" dangerouslySetInnerHTML={{ __html: selectedTemplate.description || 'No description' }} />
                </div>
              </div>
              <div className="col-md-6">
                <strong className="label1">Display Options:</strong>
                <div style={{ marginTop: '10px' }}>
                  {checkboxFields.map((field) => (
                    <div key={field.key} style={{ marginBottom: '5px' }}>
                      <span style={{ fontSize: '14px' }}>
                        {field.label}: {selectedTemplate[field.key] ? '✓' : '✗'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowViewModal(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default MeritListTemplates;